import type { SessionSettings } from "@mantra/shared";
import { createAmbient, type Ambient } from "./ambient";

/**
 * Сессия собирается на клиенте из отдельных клипов: пользователь меняет паузы и повторы
 * без перегенерации аудио, а общие фразы переиспользуются из кэша.
 */
export interface TimelineEvent {
  index: number; // номер аффирмации
  at: number; // секунды от начала сессии
  duration: number;
}

export function buildTimeline(durations: number[], s: Pick<SessionSettings, "repeats" | "gapSec" | "loopMinutes">): TimelineEvent[] {
  const events: TimelineEvent[] = [];
  if (!durations.length) return events;
  let t = 0;
  const pass = () => {
    durations.forEach((d, index) => {
      for (let r = 0; r < s.repeats; r++) {
        events.push({ index, at: t, duration: d });
        t += d + s.gapSec;
      }
    });
  };
  pass();
  const target = s.loopMinutes * 60;
  while (target > 0 && t < target) pass();
  return events;
}

export function timelineLength(events: TimelineEvent[]): number {
  const last = events.at(-1);
  return last ? last.at + last.duration : 0;
}

let shared: AudioContext | null = null;
let stale = false;

/**
 * На iPhone контекст, созданный до записи с микрофона, после неё может звучать в динамик у уха
 * или молчать. Помечаем его устаревшим — следующий запуск звука создаст новый.
 */
export function markAudioContextStale() {
  stale = true;
}

/** Новый контекст взамен старого: вызывать внутри нажатия, чтобы браузер разрешил звук */
export function resetAudioContext(): AudioContext {
  const old = shared;
  shared = null;
  stale = false;
  void old?.close().catch(() => {});
  return audioContext();
}

/** Один контекст на приложение. Создаём заранее в suspended — декодирование не ждёт жеста пользователя */
export function audioContext(): AudioContext {
  if (stale) return resetAudioContext();
  if (!shared || shared.state === "closed") {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    shared = new Ctor({ latencyHint: "playback" });
  }
  return shared;
}

export type PlayerState = "idle" | "playing" | "paused" | "ended";

const LOOKAHEAD_SEC = 1.5;
const TICK_MS = 120;

export class SessionPlayer {
  private ctx = audioContext();
  private voiceBus: GainNode;
  private analyser: AnalyserNode;
  private ambient: Ambient | null = null;
  private timeline: TimelineEvent[];
  private scheduled = 0;
  private startAt = 0;
  private timer: ReturnType<typeof setInterval> | undefined;
  private sources = new Set<AudioBufferSourceNode>();
  private levelData: Uint8Array<ArrayBuffer>;
  state: PlayerState = "idle";
  readonly total: number;

  constructor(
    private readonly buffers: AudioBuffer[],
    private readonly settings: SessionSettings,
    private readonly onChange: (s: PlayerState) => void,
  ) {
    this.timeline = buildTimeline(
      buffers.map((b) => b.duration),
      settings,
    );
    this.total = timelineLength(this.timeline);
    this.voiceBus = this.ctx.createGain();
    this.voiceBus.gain.value = settings.voiceVolume;
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 256;
    this.levelData = new Uint8Array(new ArrayBuffer(this.analyser.fftSize));
    this.voiceBus.connect(this.analyser);
    this.voiceBus.connect(this.ctx.destination);
  }

  async play() {
    await this.ctx.resume();
    if (this.state === "idle" || this.state === "ended") {
      this.reset();
      this.ambient = createAmbient(this.ctx, this.settings.ambient, this.settings.ambientVolume);
      this.ambient?.output.connect(this.ctx.destination);
      // небольшой вход: фон успевает подняться до первой фразы
      this.startAt = this.ctx.currentTime + (this.ambient ? 2.5 : 0.3);
      this.timer = setInterval(() => this.tick(), TICK_MS);
      this.tick();
    }
    this.set("playing");
  }

  async pause() {
    if (this.state !== "playing") return;
    await this.ctx.suspend(); // время контекста останавливается — расписание сохраняется
    this.set("paused");
  }

  stop() {
    clearInterval(this.timer);
    this.timer = undefined;
    this.sources.forEach((s) => {
      try {
        s.stop();
      } catch {
        /* noop */
      }
    });
    this.sources.clear();
    this.ambient?.stop(0.8);
    this.ambient = null;
    if (this.state !== "ended") this.set("idle");
  }

  setVoiceVolume(v: number) {
    this.voiceBus.gain.setTargetAtTime(v, this.ctx.currentTime, 0.1);
  }

  /** Позиция в секундах от начала первой фразы */
  position(): number {
    if (this.state === "idle") return 0;
    return Math.max(0, Math.min(this.total, this.ctx.currentTime - this.startAt));
  }

  /** Текущая (или последняя прозвучавшая) аффирмация */
  currentIndex(): number {
    const p = this.position();
    let idx = this.timeline[0]?.index ?? 0;
    for (const e of this.timeline) {
      if (e.at > p) break;
      idx = e.index;
    }
    return idx;
  }

  /** Громкость голоса 0..1 — для анимации */
  level(): number {
    this.analyser.getByteTimeDomainData(this.levelData);
    let sum = 0;
    for (const v of this.levelData) sum += (v - 128) ** 2;
    return Math.min(1, Math.sqrt(sum / this.levelData.length) / 40);
  }

  private reset() {
    this.scheduled = 0;
  }

  private tick() {
    const horizon = this.ctx.currentTime + LOOKAHEAD_SEC;
    while (this.scheduled < this.timeline.length) {
      const e = this.timeline[this.scheduled]!;
      const when = this.startAt + e.at;
      if (when > horizon) break;
      const src = this.ctx.createBufferSource();
      src.buffer = this.buffers[e.index]!;
      src.connect(this.voiceBus);
      src.onended = () => this.sources.delete(src);
      src.start(Math.max(when, this.ctx.currentTime));
      this.sources.add(src);
      this.scheduled++;
    }
    if (this.scheduled >= this.timeline.length && this.ctx.currentTime > this.startAt + this.total + 0.5) {
      clearInterval(this.timer);
      this.timer = undefined;
      this.ambient?.stop(4);
      this.ambient = null;
      this.set("ended");
    }
  }

  private set(s: PlayerState) {
    this.state = s;
    this.onChange(s);
  }
}

/** Короткое прослушивание одного клипа (превью голоса) */
export async function playOnce(buffer: AudioBuffer): Promise<void> {
  const ctx = audioContext();
  await ctx.resume();
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(ctx.destination);
  await new Promise<void>((resolve) => {
    src.onended = () => resolve();
    src.start();
  });
}
